// Simple log levels
export enum LogLevel {
  DEBUG = 0,
  INFO = 1,
  WARNING = 2,
  ERROR = 3,
  CRITICAL = 4,
}

// Simple context interface
export interface LogContext {
  [key: string]: any;
}

// Simplified logger class
export class Logger {
  private debugMode: boolean;

  constructor() {
    this.debugMode = import.meta.env.DEV || import.meta.env.MODE === 'development';
    console.info(
      `Logger initialized in ${import.meta.env.MODE} mode, debugMode: ${this.debugMode}`
    );
  }

  // Core logging method
  log(level: LogLevel, message: string, error?: Error | unknown, context: LogContext = {}): void {
    // Skip debug logs in production
    if (level === LogLevel.DEBUG && !this.debugMode) return;

    // Format the message
    const timestamp = new Date().toISOString();
    const module = context.module || 'unknown';
    const func = context.function || 'unknown';

    // Format context
    const contextStr = Object.entries(context)
      .filter(([k, v]) => k !== 'module' && k !== 'function' && v !== undefined)
      .map(([k, v]) => `${k}=${typeof v === 'object' ? JSON.stringify(v) : v}`)
      .join(' ');

    // Format error if present
    let errorInfo = '';
    if (error) {
      if (error instanceof Error) {
        errorInfo = ` | error=${error.message} stack=${error.stack || 'N/A'}`;
      } else {
        errorInfo = ` | error=${String(error)}`;
      }
    }

    const formattedMessage = `[${timestamp}] [${LogLevel[level]}] [${module}:${func}] ${message}${errorInfo}${contextStr ? ` | ${contextStr}` : ''}`;

    // Log to console with appropriate level
    switch (level) {
      case LogLevel.DEBUG:
        console.debug(formattedMessage);
        break;
      case LogLevel.INFO:
        console.info(formattedMessage);
        break;
      case LogLevel.WARNING:
        console.warn(formattedMessage);
        break;
      case LogLevel.ERROR:
        console.error(formattedMessage);
        break;
    }
  }

  // Convenience methods
  debug(message: string, context: LogContext = {}): void {
    this.log(LogLevel.DEBUG, message, undefined, context);
  }

  info(message: string, context: LogContext = {}): void {
    this.log(LogLevel.INFO, message, undefined, context);
  }

  warning(message: string, context: LogContext = {}): void {
    this.log(LogLevel.WARNING, message, undefined, context);
  }

  error(message: string, error?: Error | unknown, context: LogContext = {}): void {
    this.log(LogLevel.ERROR, message, error, context);
  }

  critical(message: string, error?: Error | unknown, context: LogContext = {}): void {
    this.log(LogLevel.CRITICAL, message, error, context);
  }

  // Simple performance tracking
  private performanceMarkers: Map<string, number> = new Map();

  startPerformanceMarker(label: string): void {
    this.performanceMarkers.set(label, performance.now());
    this.debug(`Performance marker started: ${label}`, { function: 'startPerformanceMarker' });
  }

  /**
   * Times an async operation: runs `fn`, logs how long it took, and returns
   * its result (re-throwing any error after recording the duration).
   */
  async trackPerformance<T>(
    label: string,
    fn: () => Promise<T>,
    context: LogContext = {}
  ): Promise<T> {
    const start = performance.now();
    try {
      return await fn();
    } finally {
      const duration = performance.now() - start;
      this.debug(`Performance: ${label}`, {
        ...context,
        duration: `${duration.toFixed(2)}ms`,
        function: 'trackPerformance',
      });
    }
  }

  endPerformanceMarker(label: string, context: LogContext = {}): number {
    const start = this.performanceMarkers.get(label);
    if (!start) {
      this.warning(`No performance marker found for label: ${label}`, {
        ...context,
        function: 'endPerformanceMarker',
      });
      return 0;
    }

    const duration = performance.now() - start;
    this.performanceMarkers.delete(label);

    this.debug(`Performance: ${label}`, {
      ...context,
      duration: `${duration.toFixed(2)}ms`,
      function: 'endPerformanceMarker',
    });

    return duration;
  }
}

// Export singleton instance
export const logger = new Logger();
